import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle, CheckCircle2, Circle, FileSpreadsheet, GitCompareArrows,
  ShieldCheck, Wallet, ExternalLink, X, Eye,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Empty } from "@/components/app/Primitives";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const php = (n) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(n || 0));

/* ---------------------------- small UI atoms ---------------------------- */

function StatusBadge({ value }) {
  return (
    <Badge variant="outline" className="capitalize">
      {String(value || "unknown").replaceAll("_", " ")}
    </Badge>
  );
}

function CheckRow({ item }) {
  const ok = !!item.ok;
  return (
    <div className="flex items-start gap-2 rounded-lg border p-3">
      {ok ? (
        <CheckCircle2 className="mt-0.5 size-4 text-emerald-600" />
      ) : (
        <AlertCircle className="mt-0.5 size-4 text-amber-600" />
      )}
      <div>
        <div className="text-sm font-medium">{item.label}</div>
        <div className="text-xs text-muted-foreground">
          {item.severity}
          {item.meta?.issue_count ? ` · ${item.meta.issue_count} issue(s)` : ""}
        </div>
      </div>
    </div>
  );
}

function PreflightIssue({ issue, onOpen }) {
  const bad = !issue.ok;
  return (
    <button
      type="button"
      onClick={() => onOpen(issue)}
      className={`w-full rounded-lg border p-3 text-left hover:bg-muted/60 ${
        bad && issue.severity === "critical"
          ? "bg-red-50 border-red-200"
          : bad
          ? "bg-yellow-50 border-yellow-200"
          : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{issue.label}</div>
          <div className="text-xs text-muted-foreground capitalize">
            {issue.severity} · {issue.count || 0} issue(s)
          </div>
          {issue.message && (
            <div className="mt-1 text-xs text-muted-foreground">{issue.message}</div>
          )}
          {issue.action?.hint && (
            <div className="mt-1 text-xs text-muted-foreground">{issue.action.hint}</div>
          )}
        </div>
        {bad ? (
          <AlertCircle className="size-4 text-red-600" />
        ) : (
          <CheckCircle2 className="size-4 text-emerald-600" />
        )}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {issue.employees?.length || 0} affected record(s)
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-medium">
          Open Workspace <ExternalLink className="size-3" />
        </span>
      </div>
    </button>
  );
}

function ExceptionDrawer({ issue, onClose, onNavigate }) {
  if (!issue) return null;
  const rows = issue.employees || [];
  const action = issue.action || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div
        className="ml-auto h-full w-full max-w-3xl overflow-auto bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-background p-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Payroll Exception Workspace
            </div>
            <h2 className="text-xl font-semibold">{issue.label}</h2>
            <div className="mt-1 text-sm text-muted-foreground capitalize">
              {issue.severity} · {issue.count || 0} issue(s)
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border p-2 hover:bg-muted"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div
            className={`rounded-lg border p-4 ${
              issue.severity === "critical"
                ? "border-red-200 bg-red-50"
                : "border-yellow-200 bg-yellow-50"
            }`}
          >
            <div className="text-sm font-medium">
              {issue.message || action.hint || "Review the affected records before continuing payroll."}
            </div>
            {action.hint && (
              <div className="mt-1 text-sm text-muted-foreground">{action.hint}</div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => onNavigate(action.route || "/payroll/control")}>
              {action.label || "Open Module"}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Payroll
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Affected Records</CardTitle>
            </CardHeader>
            <CardContent>
              {!rows.length && (
                <Empty
                  title="No detailed records"
                  subtitle="This exception has a count but no record-level payload."
                />
              )}
              {!!rows.length && (
                <div className="overflow-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-2 text-left">Employee No.</th>
                        <th className="p-2 text-left">Employee / Record</th>
                        <th className="p-2 text-left">Details</th>
                        <th className="p-2 text-left">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={`${issue.key}-${idx}`} className="border-t">
                          <td className="p-2">{r.employee_no || r.employee_id || "-"}</td>
                          <td className="p-2">
                            {r.employee_name || r.work_date || r.id || "Affected record"}
                          </td>
                          <td className="p-2 text-muted-foreground">
                            {r.missing ? `Missing ${r.missing}` : ""}
                            {r.scheduled_workdays ? `Scheduled workdays: ${r.scheduled_workdays}` : ""}
                            {r.attendance_days ? ` · Attendance days: ${r.attendance_days}` : ""}
                            {r.count ? `Count: ${r.count}` : ""}
                          </td>
                          <td className="p-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onNavigate(action.route || "/payroll/control")}
                            >
                              {action.label || "Open"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function AuditReplayDrawer({ data, onClose }) {
  if (!data) return null;
  const emp = data.employee || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div
        className="ml-auto h-full w-full max-w-4xl overflow-auto bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-background p-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Payroll Audit Replay (Immutable Evidence)
            </div>
            <h2 className="text-xl font-semibold">
              {emp.employee_no || "—"} · {emp.first_name} {emp.last_name}
            </h2>
            <div className="mt-1 text-xs text-muted-foreground">{data.audit_note}</div>
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Gross Pay</CardTitle>
              </CardHeader>
              <CardContent className="text-xl font-semibold">
                {php(data.calculation?.gross_pay)}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Total Deductions</CardTitle>
              </CardHeader>
              <CardContent className="text-xl font-semibold">
                {php(data.calculation?.total_deductions)}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Net Pay</CardTitle>
              </CardHeader>
              <CardContent className="text-xl font-semibold">
                {php(data.calculation?.net_pay)}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-3 text-xs">
            <div className="rounded border p-3">
              <div className="text-muted-foreground">Rule Version</div>
              <div className="mt-1 font-medium">{data.rule_version || "—"}</div>
            </div>
            <div className="rounded border p-3">
              <div className="text-muted-foreground">Computed At</div>
              <div className="mt-1 font-medium">{data.computed_at || "—"}</div>
            </div>
            <div className="rounded border p-3">
              <div className="text-muted-foreground">Locked At</div>
              <div className="mt-1 font-medium">{data.locked_at || "—"}</div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Attendance Snapshot</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm mb-2">
                Rows: {data.attendance_snapshot?.count || 0} · Stored at time of payroll lock — not live data.
              </div>
              <div className="overflow-auto max-h-72 rounded border">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr className="text-left">
                      <th className="p-2">Date</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">Time In</th>
                      <th className="p-2">Time Out</th>
                      <th className="p-2">Hours</th>
                      <th className="p-2">Approved At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.attendance_snapshot?.rows || []).map((r, i) => (
                      <tr key={r.attendance_id || i} className="border-t">
                        <td className="p-2">{r.work_date}</td>
                        <td className="p-2">{r.status}</td>
                        <td className="p-2">{r.time_in || "—"}</td>
                        <td className="p-2">{r.time_out || "—"}</td>
                        <td className="p-2">{r.hours_worked}</td>
                        <td className="p-2">{r.approved_at || "—"}</td>
                      </tr>
                    ))}
                    {!data.attendance_snapshot?.rows?.length && (
                      <tr>
                        <td colSpan={6} className="p-3 text-center text-muted-foreground">
                          No attendance snapshot rows stored on this payroll item.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Formula Inputs</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="overflow-auto rounded bg-muted p-3 text-xs">
                  {JSON.stringify(data.formula_inputs, null, 2)}
                </pre>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Calculation Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="overflow-auto rounded bg-muted p-3 text-xs">
                  {JSON.stringify(data.calculation, null, 2)}
                </pre>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelinePanel({ rows = [] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Payroll Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div key={`${row.key}-${idx}`} className="flex gap-3">
              <div
                className={`mt-1 size-3 rounded-full ${
                  row.done ? "bg-emerald-500" : "bg-muted"
                }`}
              />
              <div className="flex-1 border-b pb-3">
                <div className="text-sm font-medium">{row.label}</div>
                <div className="text-xs text-muted-foreground">
                  {row.timestamp
                    ? new Date(row.timestamp).toLocaleString()
                    : row.done
                    ? "Completed"
                    : "Pending"}
                </div>
                {row.detail && (
                  <div className="mt-1 text-xs text-muted-foreground">{row.detail}</div>
                )}
              </div>
            </div>
          ))}
          {!rows.length && (
            <div className="text-sm text-muted-foreground">No timeline events yet.</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Workflow({ rows = [] }) {
  return (
    <div className="grid gap-2">
      {rows.map((w, idx) => (
        <div
          key={`${w.step}-${idx}`}
          className="flex items-center gap-3 rounded-lg border p-3"
        >
          {w.status === "done" ? (
            <CheckCircle2 className="size-4 text-emerald-600" />
          ) : (
            <Circle className="size-4 text-muted-foreground" />
          )}
          <div className="flex-1">
            <div className="text-sm font-medium capitalize">
              {String(w.step).replaceAll("_", " ")}
            </div>
            <div className="text-xs text-muted-foreground">
              {w.status}
              {w.email ? ` · ${w.email}` : ""}
            </div>
          </div>
        </div>
      ))}
      {!rows.length && (
        <div className="rounded-lg border p-3 text-sm text-muted-foreground">
          Workflow not yet started.
        </div>
      )}
    </div>
  );
}

/* ------------------------------ main page ------------------------------ */

const SCEN_STATUS_TONE = {
  draft: "text-muted-foreground",
  computed: "text-blue-600",
  reviewed: "text-amber-600",
  approved: "text-emerald-600",
  rejected: "text-destructive",
  locked: "text-emerald-700",
  released: "text-emerald-700",
  paid: "text-emerald-700",
};

function ScenarioLabPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const load = () => api.get("/scenario-lab/status").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!window.confirm("Reset the 3 test scenarios? This wipes and rebuilds the isolated Scenario Lab company so you can replay them. Real payroll data is untouched.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scenario-lab/reset");
      setData(data);
      toast.success("Scenarios reset — ready to replay");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const openRun = (companyId, runId) => {
    localStorage.setItem("active_company_id", companyId);
    window.location.href = `/payroll/runs/${runId}`;
  };

  if (!data) return null;

  return (
    <Card className="mb-4 border-dashed">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <button className="flex items-center gap-2 text-left" onClick={() => setOpen((v) => !v)}>
          <CardTitle className="text-base">🧪 Payroll Test Scenarios</CardTitle>
          <Badge variant="outline">{data.seeded ? "seeded" : "not seeded"}</Badge>
        </button>
        <Button size="sm" variant="outline" onClick={reset} disabled={busy} data-testid="scenario-reset">
          {busy ? "Resetting…" : "Reset & Replay"}
        </Button>
      </CardHeader>
      {open && (
        <CardContent>
          <p className="mb-3 text-xs text-muted-foreground">
            Three replayable payroll runs in an isolated <b>{data.company_name}</b> company. Open one to walk it through the control workflow, then Reset to replay.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {(data.scenarios || []).map((s) => (
              <div key={s.key} className="rounded-lg border p-3" data-testid={`scenario-${s.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold">{s.title}</div>
                  <span className={`text-xs font-semibold capitalize ${SCEN_STATUS_TONE[s.status] || "text-muted-foreground"}`}>{String(s.status).replaceAll("_", " ")}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{s.description}</div>
                <div className="mt-2 rounded bg-muted/60 p-2 text-[11px] text-muted-foreground"><b>How to play:</b> {s.playbook}</div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground">{s.period}</span>
                  {s.run_id && (
                    <Button size="sm" variant="secondary" onClick={() => openRun(data.company_id, s.run_id)} data-testid={`scenario-open-${s.key}`}>
                      Open run <ExternalLink className="ml-1 size-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function PayrollControlCenter() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);
  const [revisionCompare, setRevisionCompare] = useState(null);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [activeException, setActiveException] = useState(null);
  const [auditReplay, setAuditReplay] = useState(null);

  const loadRuns = async () => {
    try {
      const { data } = await api.get("/payroll/runs");
      const active = (data.items || []).filter(
        (r) => !["voided", "reversed", "cancelled"].includes(r.status)
      );
      setRuns(active);
      if (!selected && active[0]) setSelected(active[0].id);
    } catch (e) {
      toast.error("Failed to load payroll runs");
    }
  };

  const loadWorkspace = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/payroll/control/runs/${id}/workspace`);
      setWorkspace(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payroll workspace");
    } finally {
      setLoading(false);
    }
  };

  const loadRevisions = async (id) => {
    if (!id) return;
    setRevisionLoading(true);
    try {
      const { data } = await api.get(`/payroll/control/runs/${id}/revisions`);
      const rows = data.items || [];
      setRevisions(rows);
      if (rows[0]) await compareRevisionToCurrent(id, rows[0].revision_no);
      else setRevisionCompare(null);
    } catch (e) {
      setRevisions([]);
      setRevisionCompare(null);
    } finally {
      setRevisionLoading(false);
    }
  };

  const compareRevisionToCurrent = async (id, revisionNo) => {
    if (!id || !revisionNo) return;
    setRevisionLoading(true);
    try {
      const { data } = await api.get(
        `/payroll/control/runs/${id}/revisions/${revisionNo}/compare-current`
      );
      setRevisionCompare(data);
    } catch (e) {
      setRevisionCompare(null);
    } finally {
      setRevisionLoading(false);
    }
  };

  const openExceptionRoute = (route) => {
    setActiveException(null);
    navigate(route || "/payroll/control");
  };

  const openAuditReplay = async (itemId) => {
    try {
      const { data } = await api.get(`/payroll/items/${itemId}/audit-replay`);
      setAuditReplay(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit replay");
    }
  };

  const gateAction = async (action, payload = {}) => {
    if (!selected) return;
    try {
      await api.post(`/payroll/control/runs/${selected}/${action}`, payload);
      toast.success(`Payroll ${action} completed`);
      await loadWorkspace(selected);
      await loadRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} payroll`);
    }
  };

  useEffect(() => {
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (selected) {
      loadWorkspace(selected);
      loadRevisions(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const run = workspace?.run;
  const summary = workspace?.summary || {};
  const allowed = workspace?.allowed_actions || [];
  const preflight = workspace?.preflight || {};

  const preflightIssues = preflight.issues || [];
  const criticalIssues = useMemo(
    () => preflightIssues.filter((i) => i.severity === "critical" && !i.ok),
    [preflightIssues]
  );
  const warningIssues = useMemo(
    () => preflightIssues.filter((i) => i.severity === "warning" && !i.ok),
    [preflightIssues]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payroll Control Center"
        subtitle="Operational command center for payroll review, validation, audit, accounting, BIR, and remittance readiness."
      />

      <ScenarioLabPanel />

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Active Payroll Runs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {!runs.length && (
              <Empty title="No active runs" subtitle="Create a payroll run first." />
            )}
            {runs.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r.id)}
                className={`w-full rounded-lg border p-3 text-left hover:bg-muted ${
                  selected === r.id ? "bg-muted" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm">
                    {r.period_start} → {r.period_end}
                  </div>
                  <StatusBadge value={r.status} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {php(r.total_net)} net · {r.employee_count || 0} employees
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {loading && !workspace && (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground">
                Loading workspace…
              </CardContent>
            </Card>
          )}

          {!workspace && !loading && (
            <Empty title="No workspace selected" subtitle="Select a payroll run." />
          )}

          {workspace && run && (
            <>
              {/* Run summary header */}
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle>
                      {run.period_start} → {run.period_end}
                    </CardTitle>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <StatusBadge value={run.status} />
                      {run.inputs_frozen && <Badge>Inputs Frozen</Badge>}
                      {run.locked && <Badge>Locked</Badge>}
                      {run.final_locked && <Badge variant="destructive">Final Locked</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button asChild variant="outline">
                      <Link to={`/payroll/runs/${run.id}`}>Open Run</Link>
                    </Button>
                    {allowed.includes("view_journal") && (
                      <Button
                        variant="outline"
                        onClick={() =>
                          window.open(`/api/payroll/finance/runs/${run.id}/gl-export.csv`, "_blank")
                        }
                      >
                        GL CSV
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() =>
                        window.open(`/api/payroll/finance/runs/${run.id}/bank-export.csv`, "_blank")
                      }
                    >
                      Bank CSV
                    </Button>
                    <Button variant="outline" onClick={() => loadRevisions(run.id)}>
                      <GitCompareArrows className="mr-2 size-4" /> Revisions
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-4">
                  <div className="rounded-lg border p-4">
                    <div className="text-xs text-muted-foreground">Gross</div>
                    <div className="text-xl font-semibold">{php(summary.gross)}</div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-xs text-muted-foreground">Deductions</div>
                    <div className="text-xl font-semibold">{php(summary.deductions)}</div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-xs text-muted-foreground">Net Pay</div>
                    <div className="text-xl font-semibold">{php(summary.net)}</div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-xs text-muted-foreground">Employees</div>
                    <div className="text-xl font-semibold">{summary.employees || 0}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Approval Gate */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="size-4" /> Approval Gate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-4">
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Reviewed</div>
                      <div className="text-lg font-semibold">
                        {workspace.approval_gate?.reviewed ? "Yes" : "No"}
                      </div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Approved</div>
                      <div className="text-lg font-semibold">
                        {workspace.approval_gate?.approved ? "Yes" : "No"}
                      </div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Critical Issues</div>
                      <div className="text-lg font-semibold">{preflight.critical_count || 0}</div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Gate Status</div>
                      <div className="text-lg font-semibold">
                        {workspace.approval_gate?.approved ? "Passed" : "Blocked"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      disabled={
                        (preflight.critical_count || 0) > 0 || workspace.approval_gate?.reviewed
                      }
                      onClick={() =>
                        gateAction("review", { notes: "Payroll reviewed from Control Center" })
                      }
                    >
                      Mark Reviewed
                    </Button>
                    <Button
                      disabled={
                        !workspace.approval_gate?.reviewed ||
                        (preflight.critical_count || 0) > 0 ||
                        workspace.approval_gate?.approved
                      }
                      onClick={() =>
                        gateAction("approve", { notes: "Payroll approved from Control Center" })
                      }
                    >
                      Approve Payroll
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={workspace.approval_gate?.approved}
                      onClick={() => {
                        const reason = window.prompt("Reject reason:");
                        if (reason) gateAction("reject", { reason });
                      }}
                    >
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Payroll Readiness / Exception Center */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="size-4" /> Payroll Readiness & Exception Center
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-6">
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Readiness</div>
                      <div className="text-2xl font-semibold">
                        {preflight.readiness_score ?? 0}%
                      </div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Integrity Score</div>
                      <div className="text-2xl font-semibold">
                        {preflight.integrity_score ?? preflight.readiness_score ?? 0}/100
                      </div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Can Compute</div>
                      <div className="text-lg font-semibold">
                        {preflight.can_compute ? "Yes" : "No"}
                      </div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Critical</div>
                      <div className="text-lg font-semibold">{preflight.critical_count || 0}</div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Warnings</div>
                      <div className="text-lg font-semibold">{preflight.warning_count || 0}</div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-muted-foreground">Employees</div>
                      <div className="text-lg font-semibold">
                        {preflight.employee_count || summary.employees || 0}
                      </div>
                    </div>
                  </div>

                  {!preflight.can_compute && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                      Payroll compute is blocked by critical preflight issues. Do not override
                      unless this is an approved exceptional payroll run.
                    </div>
                  )}

                  {!!preflight.integrity_deductions?.length && (
                    <div className="rounded-lg border p-3">
                      <div className="mb-2 text-sm font-medium">Integrity Deductions</div>
                      <div className="space-y-1">
                        {preflight.integrity_deductions.slice(0, 6).map((d) => (
                          <div
                            key={d.key}
                            className="flex items-center justify-between text-xs"
                          >
                            <span>{d.label}</span>
                            <span className="font-medium">-{d.points}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid gap-3 lg:grid-cols-2">
                    {[...criticalIssues, ...warningIssues].slice(0, 10).map((issue) => (
                      <PreflightIssue
                        key={issue.key}
                        issue={issue}
                        onOpen={setActiveException}
                      />
                    ))}
                    {!criticalIssues.length && !warningIssues.length && (
                      <div className="rounded-lg border p-3 text-sm text-muted-foreground">
                        No blocking payroll preflight issues found.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Legacy validation + workflow + timeline + audit */}
              <div className="grid gap-4 xl:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShieldCheck className="size-4" /> Legacy Validation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(workspace.validation || []).map((v) => (
                      <CheckRow key={v.key} item={v} />
                    ))}
                    {!workspace.validation?.length && (
                      <Empty title="No legacy validation results" />
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Workflow</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Workflow rows={workspace.workflow || []} />
                  </CardContent>
                </Card>

                <TimelinePanel rows={workspace.timeline || []} />

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Audit Timeline</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {!workspace.audit?.length && (
                      <Empty title="No audit events" subtitle="Events will appear here." />
                    )}
                    {(workspace.audit || []).slice(0, 10).map((a) => (
                      <div key={a.id} className="rounded-lg border p-3">
                        <div className="text-sm font-medium capitalize">
                          {String(a.event || a.action || "event").replaceAll("_", " ")}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {a.created_at}
                          {a.user_email ? ` · ${a.user_email}` : ""}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Payroll Items Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Payroll Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr className="text-left">
                          <th className="p-2">Employee</th>
                          <th className="p-2">Gross</th>
                          <th className="p-2">Deductions</th>
                          <th className="p-2">Net</th>
                          <th className="p-2">Status</th>
                          <th className="p-2">Evidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(workspace.payroll_items || []).map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="p-2">
                              <div className="font-medium">
                                {item.employee_no || "—"} · {item.employee_name || "—"}
                              </div>
                              {item.payslip_no && (
                                <div className="text-xs text-muted-foreground">
                                  Payslip: {item.payslip_no}
                                </div>
                              )}
                            </td>
                            <td className="p-2">{php(item.gross_pay)}</td>
                            <td className="p-2">{php(item.total_deductions)}</td>
                            <td className="p-2 font-medium">{php(item.net_pay)}</td>
                            <td className="p-2">
                              <StatusBadge value={item.status || "computed"} />
                            </td>
                            <td className="p-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openAuditReplay(item.id)}
                              >
                                <Eye className="mr-1 size-3" /> Replay Audit
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {!workspace.payroll_items?.length && (
                          <tr>
                            <td colSpan={6} className="py-4 text-sm text-muted-foreground text-center">
                              No payroll items computed yet. Run compute first.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Revisions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <GitCompareArrows className="size-4" /> Payroll Revisions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!revisions.length && !revisionLoading && (
                    <Empty
                      title="No revisions yet"
                      subtitle="Revision snapshots appear after recomputation."
                    />
                  )}
                  <div className="grid gap-4 xl:grid-cols-[320px_1fr]">
                    <div className="space-y-2">
                      {(revisions || []).map((rev) => (
                        <button
                          key={rev.id || rev.revision_no}
                          onClick={() => compareRevisionToCurrent(run.id, rev.revision_no)}
                          className={`w-full rounded-lg border p-3 text-left hover:bg-muted ${
                            revisionCompare?.from_revision === rev.revision_no ? "bg-muted" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-sm">
                              Revision {rev.revision_no}
                            </div>
                            <Badge variant="outline">{php(rev.total_net)}</Badge>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {rev.created_at}
                          </div>
                          <div className="mt-1 text-xs">
                            {rev.reason || "Payroll recomputed"}
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="rounded-lg border p-4">
                      {!revisionCompare ? (
                        <Empty
                          title="No comparison selected"
                          subtitle="Select a revision to compare it against current payroll."
                        />
                      ) : (
                        <div className="space-y-4">
                          <div className="grid gap-3 md:grid-cols-4">
                            <div className="rounded-lg border p-3">
                              <div className="text-xs text-muted-foreground">From Revision</div>
                              <div className="text-lg font-semibold">
                                {revisionCompare.from_revision}
                              </div>
                            </div>
                            <div className="rounded-lg border p-3">
                              <div className="text-xs text-muted-foreground">Gross Delta</div>
                              <div className="text-lg font-semibold">
                                {php(revisionCompare.delta?.gross)}
                              </div>
                            </div>
                            <div className="rounded-lg border p-3">
                              <div className="text-xs text-muted-foreground">Deduction Delta</div>
                              <div className="text-lg font-semibold">
                                {php(revisionCompare.delta?.deductions)}
                              </div>
                            </div>
                            <div className="rounded-lg border p-3">
                              <div className="text-xs text-muted-foreground">Net Delta</div>
                              <div className="text-lg font-semibold">
                                {php(revisionCompare.delta?.net)}
                              </div>
                            </div>
                          </div>

                          <div className="text-sm font-semibold">
                            Changed Employees: {revisionCompare.changed_employee_count || 0}
                          </div>

                          <div className="space-y-3">
                            {(revisionCompare.employee_diffs || []).map((d) => (
                              <div
                                key={d.employee_id || d.employee_no}
                                className="rounded-lg border p-3"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <div className="font-medium text-sm">
                                      {d.employee_no} · {d.employee_name}
                                    </div>
                                    <div className="text-xs text-muted-foreground capitalize">
                                      {d.change_type}
                                    </div>
                                  </div>
                                  <div className="text-right text-sm">
                                    <div>
                                      Gross: <b>{php(d.gross_delta)}</b>
                                    </div>
                                    <div>
                                      Net: <b>{php(d.net_delta)}</b>
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-3 grid gap-2 md:grid-cols-3">
                                  {Object.entries(d.field_diffs || {}).map(([field, diff]) => (
                                    <div key={field} className="rounded-md bg-muted p-2">
                                      <div className="text-xs font-semibold capitalize">
                                        {field.replaceAll("_", " ")}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        Before: {php(diff.before)}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        After: {php(diff.after)}
                                      </div>
                                      <div className="text-xs font-medium">
                                        Delta: {php(diff.delta)}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {!!(d.after_adjustments || []).length && (
                                  <div className="mt-3">
                                    <div className="text-xs font-semibold mb-1">
                                      Current Approved Adjustments
                                    </div>
                                    <div className="space-y-1">
                                      {(d.after_adjustments || []).map((a) => (
                                        <div
                                          key={a.id}
                                          className="text-xs rounded-md border p-2"
                                        >
                                          <b>
                                            {String(a.type || "").replaceAll("_", " ")}
                                          </b>{" "}
                                          · {php(a.amount)} ·{" "}
                                          {a.description || a.reason}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                            {!revisionCompare.employee_diffs?.length && (
                              <Empty
                                title="No payroll differences"
                                subtitle="The selected revision matches the current payroll totals."
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Journal / BIR / Remittance */}
              <div className="grid gap-4 lg:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileSpreadsheet className="size-4" /> Journal
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="text-sm">
                      Balanced: <b>{workspace.journal?.is_balanced ? "Yes" : "No"}</b>
                    </div>
                    <div className="text-sm">
                      Debit: {php(workspace.journal?.total_debit)}
                    </div>
                    <div className="text-sm">
                      Credit: {php(workspace.journal?.total_credit)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">BIR</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="text-sm">
                      Ready: <b>{workspace.bir_readiness?.ready ? "Yes" : "No"}</b>
                    </div>
                    <div className="text-sm">
                      Issues: {workspace.bir_readiness?.issue_count || 0}
                    </div>
                    <Button
                      className="mt-3"
                      variant="outline"
                      onClick={() =>
                        window.open(
                          `/api/payroll/bir/exports/alphalist/${String(run.period_start).slice(0, 4)}.csv`,
                          "_blank"
                        )
                      }
                    >
                      Export Alphalist
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wallet className="size-4" /> Remittances
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="text-sm">
                      SSS: {php(workspace.remittance_summary?.sss?.total)}
                    </div>
                    <div className="text-sm">
                      PhilHealth: {php(workspace.remittance_summary?.philhealth?.total)}
                    </div>
                    <div className="text-sm">
                      Pag-IBIG: {php(workspace.remittance_summary?.pagibig?.total)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>

      <ExceptionDrawer
        issue={activeException}
        onClose={() => setActiveException(null)}
        onNavigate={openExceptionRoute}
      />

      <AuditReplayDrawer data={auditReplay} onClose={() => setAuditReplay(null)} />
    </div>
  );
}
